import type { TimeRange } from "@nexus/widget-sdk";
import type { DashboardTimeRange, DashboardRefresh } from "./types";
import { resolveScheduledTimeRange } from "./scheduledTimeRange";

export const ANALYTICS_TIME_RANGE_OPTIONS = [
  "Last 1h",
  "Last 6h",
  "Last 12h",
  "Last 24h",
  "Last 3d",
] as const;

export type AnalyticsTimeRangeLabel = (typeof ANALYTICS_TIME_RANGE_OPTIONS)[number];

const ANALYTICS_TIME_RANGE_HOURS: Record<AnalyticsTimeRangeLabel, number> = {
  "Last 1h": 1,
  "Last 6h": 6,
  "Last 12h": 12,
  "Last 24h": 24,
  "Last 3d": 72,
};

export function buildAnalyticsTimeRange(
  label: AnalyticsTimeRangeLabel,
  referenceTime = Date.now(),
): TimeRange {
  const to = new Date(referenceTime).toISOString();
  const from = new Date(
    referenceTime - ANALYTICS_TIME_RANGE_HOURS[label] * 3_600_000,
  ).toISOString();

  return { from, to, label };
}

export function getAnalyticsTimeRangeShortLabel(label: AnalyticsTimeRangeLabel): string {
  return label.replace("Last ", "");
}

/** A dashboard's own period beside the short presets: "7 days" rather than "Last 7 days". */
export function shortPeriodLabel(label: string): string {
  return label.replace(/^Last /, "");
}

const UNIT_MS = { minute: 60_000, hour: 3_600_000, day: 86_400_000, week: 604_800_000 };

export function calendarWeekStart(referenceTime: number): number {
  const dayStart = Math.floor(referenceTime / UNIT_MS.day) * UNIT_MS.day;
  return dayStart - ((new Date(dayStart).getUTCDay() + 6) % 7) * UNIT_MS.day;
}

export function canChooseDashboardTimeRange(
  policy: DashboardTimeRange | undefined,
  viewerControls: readonly string[] | undefined,
): policy is Extract<DashboardTimeRange, { mode: "relative" }> {
  return policy?.mode === "relative" && (viewerControls ?? ["time-range"]).includes("time-range");
}

export function resolveViewerTimeRange(
  policy: DashboardTimeRange | undefined,
  viewerControls: readonly string[] | undefined,
  selection: string | null,
): DashboardTimeRange | undefined {
  if (!canChooseDashboardTimeRange(policy, viewerControls)) return policy;
  const preset = ANALYTICS_TIME_RANGE_OPTIONS.find((option) => option === selection);
  return preset ? { ...policy, amount: ANALYTICS_TIME_RANGE_HOURS[preset], unit: "hour" } : policy;
}

export function dashboardTimeRangePreset(
  policy: DashboardTimeRange,
): AnalyticsTimeRangeLabel | undefined {
  if (policy.mode !== "relative" || policy.unit === "calendar-week") return undefined;
  const duration = policy.amount * UNIT_MS[policy.unit];
  return ANALYTICS_TIME_RANGE_OPTIONS.find(
    (option) => ANALYTICS_TIME_RANGE_HOURS[option] * UNIT_MS.hour === duration,
  );
}

export function defaultTimeRange(purpose: "live" | "reporting"): DashboardTimeRange {
  return purpose === "live"
    ? { mode: "relative", amount: 6, unit: "hour", endAt: "now" }
    : { mode: "relative", amount: 7, unit: "day", endAt: "start-of-today" };
}

export function defaultRefresh(purpose: "live" | "reporting"): DashboardRefresh {
  return {
    intervalSeconds: purpose === "live" ? 60 : 86400,
    onFocus: purpose === "live" ? "always" : "if-stale",
    schedule: purpose === "live" ? "interval" : "daily-utc",
  };
}

export function resolveDashboardTimeRange(
  policy: DashboardTimeRange,
  referenceTime: number,
  event?: { event_start: string; event_end: string; event_id?: string },
): TimeRange | null {
  let from: number;
  let to: number;
  let label: string;
  if (policy.mode === "relative") {
    if (policy.unit === "calendar-week") {
      const weekStart = calendarWeekStart(referenceTime);
      const includeCurrentWeek = policy.includeCurrentWeek ?? true;
      to = includeCurrentWeek
        ? policy.endAt === "start-of-today"
          ? Math.floor(referenceTime / UNIT_MS.day) * UNIT_MS.day
          : referenceTime
        : weekStart;
      from = weekStart - (policy.amount - (includeCurrentWeek ? 1 : 0)) * UNIT_MS.week;
      label = `Last ${policy.amount} calendar week${policy.amount === 1 ? "" : "s"}${includeCurrentWeek ? " (including current)" : " (complete)"}`;
    } else {
      to =
        policy.endAt === "start-of-today"
          ? Math.floor(referenceTime / UNIT_MS.day) * UNIT_MS.day
          : referenceTime;
      from = to - policy.amount * UNIT_MS[policy.unit];
      label = `Last ${policy.amount} ${policy.unit}${policy.amount === 1 ? "" : "s"}`;
    }
  } else if (policy.mode === "absolute") {
    from = Date.parse(policy.start);
    to = Date.parse(policy.end);
    label = "Fixed period";
  } else if (policy.mode === "scheduled") {
    const resolved = resolveScheduledTimeRange(policy, referenceTime);
    if (!resolved) return null;
    ({ from, to } = resolved);
    label = "Scheduled window";
  } else {
    if (!event) return null;
    from = Date.parse(event.event_start) - policy.beforeSeconds * 1000;
    to = Date.parse(event.event_end) + policy.afterSeconds * 1000;
    label = "Event period";
  }
  const currentCalendarWeek =
    policy.mode === "relative" &&
    policy.unit === "calendar-week" &&
    (policy.includeCurrentWeek ?? true);
  if (
    !Number.isFinite(from) ||
    !Number.isFinite(to) ||
    to < from ||
    (to === from && !currentCalendarWeek)
  )
    return null;
  return {
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
    label,
    key: JSON.stringify([
      policy,
      event?.event_id,
      policy.mode === "relative" && policy.unit === "calendar-week"
        ? [from, currentCalendarWeek && policy.endAt === "now" ? null : to]
        : policy.mode === "relative" && policy.endAt === "now"
          ? null
          : [from, to],
    ]),
  };
}
